/**
 * Primary Theme Hook for Deckspace
 *
 * This is the RECOMMENDED way to manage themes in Deckspace apps.
 * It works standalone without requiring a context provider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { colorScheme, toggleColorScheme, themeKey } = useTheme();
 *
 *   return (
 *     <div key={themeKey}>
 *       <button onClick={toggleColorScheme}>
 *         {colorScheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * Note: There is also a ThemeProvider-based useTheme in ./theme/ThemeProvider.tsx
 * with a different interface. That one requires wrapping your app in <ThemeProvider>.
 * For most use cases, this standalone hook is preferred.
 */

import { useState, useEffect, useCallback } from 'react';
import type { ThemeName, ColorScheme } from '@deckspace/types';

// Theme storage key
const SCHEME_STORAGE_KEY = 'deckspace-color-scheme';
const DEFAULT_SCHEME: ColorScheme = 'dark';

/**
 * Get stored color scheme from localStorage
 */
export function getStoredScheme(): ColorScheme {
  try {
    const stored = localStorage.getItem(SCHEME_STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return DEFAULT_SCHEME;
}

/**
 * Apply theme to document root
 * Uses .dark class on <html> (shadcn/ui convention)
 */
export function applyTheme(theme: ThemeName): void {
  const root = document.documentElement;

  if (theme === 'deckspace-dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export interface UseThemeReturn {
  /** Current effective theme name */
  themeName: ThemeName;
  /** Current color scheme (light/dark) */
  colorScheme: ColorScheme;
  /** Key that changes when theme changes (for forcing re-renders) */
  themeKey: number;
  /** Change theme by name */
  setTheme: (theme: ThemeName) => void;
  /** Set color scheme directly */
  setColorScheme: (scheme: ColorScheme) => void;
  /** Toggle between light and dark color schemes */
  toggleColorScheme: () => void;
}

/**
 * Get initial color scheme from DOM (if set by theme-init script) or storage
 */
function getInitialScheme(): ColorScheme {
  // Check if theme is already applied by inline theme-init script
  const isDark = document.documentElement.classList.contains('dark');
  if (isDark) return 'dark';
  // Check if explicitly light (no .dark class but document is loaded)
  if (document.readyState !== 'loading') return 'light';
  // Fall back to storage
  return getStoredScheme();
}

/**
 * Primary hook for theme management across all Deckspace apps.
 *
 * Features:
 * - Works standalone (no provider required)
 * - Persists to localStorage
 * - Syncs with theme-init script (prevents flash of wrong theme)
 * - Provides themeKey for forcing re-renders on theme change
 *
 * @returns Theme state and controls
 */
export function useTheme(): UseThemeReturn {
  // Initialize from DOM if already set (by theme-init script), otherwise from storage
  const [colorScheme, setColorSchemeState] = useState<ColorScheme>(getInitialScheme);
  const [themeKey, setThemeKey] = useState(0);

  // Compute the theme name from scheme
  const themeName: ThemeName = colorScheme === 'dark' ? 'deckspace-dark' : 'deckspace-light';

  // Apply theme on mount and when it changes
  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    const shouldBeDark = themeName === 'deckspace-dark';
    // Only apply if different (theme-init may have already set it)
    if (isDark !== shouldBeDark) {
      applyTheme(themeName);
    }
    localStorage.setItem(SCHEME_STORAGE_KEY, colorScheme);
    // Increment themeKey to force chart re-renders
    setThemeKey((k) => k + 1);
  }, [themeName, colorScheme]);

  // Handle theme change (parses theme name into scheme)
  const setTheme = useCallback((newTheme: ThemeName) => {
    setColorSchemeState(newTheme === 'deckspace-dark' ? 'dark' : 'light');
  }, []);

  const setColorScheme = useCallback((scheme: ColorScheme) => {
    setColorSchemeState(scheme);
  }, []);

  const toggleColorScheme = useCallback(() => {
    setColorSchemeState((s) => (s === 'dark' ? 'light' : 'dark'));
  }, []);

  return {
    themeName,
    colorScheme,
    themeKey,
    setTheme,
    setColorScheme,
    toggleColorScheme,
  };
}
